import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBookVisit } from '@/context/BookVisitContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteData } from '@/context/SiteDataContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b771c267-6e6a-452f-b6b8-f219955187da/artifacts/a7yev6rp_Shree%20Bajrang%2055.png";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { openModal } = useBookVisit();
  const { lang, setLang, t, pickLocale } = useLanguage();
  const { categories, siteSettings } = useSiteData();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMenuOpen(false); }, [location]);

  const navItems = [
    { label: t('nav.home', 'Home'), href: '/' },
    { label: t('nav.about', 'About'), href: '/about' },
    { label: t('nav.projects', 'Projects'), href: '/projects' },
    ...categories.map((c) => ({ label: pickLocale(c, 'name', c.name_en), href: `/category/${c.slug}` })),
    { label: t('nav.blog', 'Blog'), href: '/blog' },
    { label: t('nav.land_converter', 'Land Converter'), href: '/land-size-converter' },
    { label: t('nav.loan_calculator', 'Loan Calculator'), href: '/loan-calculator' },
    { label: t('nav.careers', 'Careers'), href: '/careers' },
    { label: t('nav.contact', 'Contact'), href: '/contact' },
  ];

  const phone = (siteSettings?.contact_phone || '+91 7581061649').replace(/\s+/g, '');
  const whatsapp = siteSettings?.contact_whatsapp || '919131709809';

  const toggleLang = () => setLang(lang === 'en' ? 'hi' : 'en');

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img src={LOGO_URL} alt="Shree Bajrang Developers" className="h-10 lg:h-14 w-auto object-contain" />
          </Link>

          <nav className="hidden xl:flex items-center space-x-0.5" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  location.pathname === item.href
                    ? 'text-brand-orange bg-orange-50'
                    : 'text-gray-700 hover:text-brand-orange hover:bg-orange-50/50'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center space-x-2 lg:space-x-3">
            <button
              onClick={toggleLang}
              className="hidden md:flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border border-gray-200 hover:border-brand-orange hover:text-brand-orange transition-colors"
              data-testid="lang-toggle-btn"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === 'en' ? 'EN' : 'हि'}
            </button>

            <div className="hidden lg:flex items-center space-x-3 text-sm">
              <a href={`tel:${phone}`} className="flex items-center space-x-1 text-gray-600 hover:text-brand-orange transition-colors">
                <Phone className="h-4 w-4" />
                <span>{(siteSettings?.contact_phone || '7581061649').replace('+91 ', '')}</span>
              </a>
            </div>

            <a
              href={`https://wa.me/${whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center justify-center w-10 h-10 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
            </a>

            <Button
              onClick={() => openModal()}
              className="hidden sm:flex bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full px-4 lg:px-6 py-2 text-sm font-medium hover:shadow-lg transition-shadow"
              data-testid="book-visit-btn-header"
            >
              {t('cta.book_visit', 'Book a Visit')}
            </Button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="xl:hidden py-4 border-t" data-testid="mobile-nav">
            <nav className="flex flex-col space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-4 py-3 text-base font-medium rounded-md transition-colors ${
                    location.pathname === item.href
                      ? 'text-brand-orange bg-orange-50'
                      : 'text-gray-700 hover:text-brand-orange hover:bg-orange-50/50'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t flex flex-col space-y-3 px-4">
              <button
                onClick={toggleLang}
                className="self-start flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border"
              >
                <Globe className="h-3.5 w-3.5" /> {lang === 'en' ? 'English' : 'हिंदी'}
              </button>
              <a href={`tel:${phone}`} className="flex items-center space-x-1 text-gray-600">
                <Phone className="h-4 w-4" />
                <span>{phone}</span>
              </a>
              <Button
                onClick={() => openModal()}
                className="bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full"
              >
                {t('cta.book_visit', 'Book a Visit')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
